"use client";

import { useContext, useEffect, useMemo, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  isAppInsightsEnabled,
  trackAppInsightsEvent,
} from "@/lib/analytics/appInsightsClient";
import { AppContext } from "@/contextApi/AppProvider";
import { AppContextType } from "@/interFace/general";

const HEARTBEAT_INTERVAL_MS = 25000;
const SESSION_STORAGE_KEY = "presence-session-id";

const getPresenceSessionId = () => {
  if (typeof window === "undefined") {
    return "server";
  }

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const generated =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  window.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
  return generated;
};

const PresenceHeartbeatMonitor = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isEnabled = useMemo(() => isAppInsightsEnabled(), []);
  const pageUrlRef = useRef<string>("/");
  const { currentUser } = useContext(AppContext) as AppContextType;
  const authState = currentUser?.uid ? "authenticated" : "anonymous";

  useEffect(() => {
    const query = searchParams?.toString();
    pageUrlRef.current = query ? `${pathname}?${query}` : pathname || "/";
  }, [pathname, searchParams]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const sessionId = getPresenceSessionId();

    const sendHeartbeat = (reason: string) => {
      if (typeof document === "undefined") {
        return;
      }

      trackAppInsightsEvent("presence_heartbeat", {
        sessionId,
        page: pageUrlRef.current,
        reason,
        authState,
        visibility: document.visibilityState,
      });
    };

    sendHeartbeat("mount");

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        sendHeartbeat("visibility");
      }
    };

    const handlePageHide = () => {
      sendHeartbeat("pagehide");
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        sendHeartbeat("interval");
      }
    }, HEARTBEAT_INTERVAL_MS);

    window.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [authState, isEnabled]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    trackAppInsightsEvent("presence_heartbeat", {
      sessionId: getPresenceSessionId(),
      page: pageUrlRef.current,
      reason: "route_change",
      authState,
      visibility:
        typeof document === "undefined" ? "unknown" : document.visibilityState,
    });
  }, [authState, isEnabled, pathname, searchParams]);

  return null;
};

export default PresenceHeartbeatMonitor;
